import { CssColours } from '@/static/colours'
import { Fonts } from '@/static/fonts'
import { ActionMenuTabs, useApplicationStore } from '@/store/application-store'
import { useProjectStore } from '@/store/project-store'
import { TextState, TextStyle } from '@/types'
import useIdGenerator from '@/composables/UseIdGenerator'
import { CSSProperties } from 'vue'

const { getId } = useIdGenerator()
 
type CustomisableTextStateProps = Partial<Omit<TextState, 'cssStyle' | 'id'>>

/**
 * This composable returns an interface for interacting with the text system
 */
export default function useTextStates () {
  const applicationStore = useApplicationStore()
  const projectStore = useProjectStore()

  /** Returns the maximum text width */
  const getMaxWidth = (): number => {
    return projectStore.resolution.width * 0.85
  }

  const defaultStyle: TextStyle = {
    textAlign: 'center',
    backgroundColor: CssColours.TRANSPARENT,
    color: CssColours.WHITE,
    fontFamily: Fonts.MODERN,
    maxWidth: getMaxWidth()
  }

  const getPaddingForFont = (fontFamily: string) => {
    switch (fontFamily) {
    case Fonts.SHOUT:
      return '21px 50px 14px 50px'
    case Fonts.TYPEWRITER:
      return '26px 50px 18px 50px'
    case Fonts.GOTHIC:
      return '17px 50px 22px 50px'
    case Fonts.MARKER:
      return '12px 50px 20px 50px'
    case Fonts.SHARP:
      return '17px 50px 20px 50px'
    case Fonts.OLD_STYLE:
      return '17px 50px 20px 50px'
    case Fonts.HEAVY:
      return '17px 50px 20px 50px'
    }
    return '20px 50px 20px 50px'
  }

  /**
   * Create a new TextState instance
   * @param textStateProps values to set the textState with
   * @returns
   */
  const createText = (textStateProps?: CustomisableTextStateProps): TextState => {
    const style = textStateProps?.style ?? defaultStyle
    return {
      id: getId(),
      text: textStateProps?.text ?? '',
      style: {
        ...style
      },
      x: textStateProps?.x ?? 0,
      y: textStateProps?.y ?? 0,
      scale: textStateProps?.scale ?? 1,
      get cssStyle (): CSSProperties {
        return {
          textAlign: this.style.textAlign,
          backgroundColor: this.style.backgroundColor,
          color: this.style.color,
          fontFamily: this.style.fontFamily,
          fontSize: '100px',
          lineHeight: '100px',
          width: 'max-content',
          maxWidth: `${this.style.maxWidth}px`,
          padding: getPaddingForFont(this.style.fontFamily)
        }
      }
    }
  }

  /**
   * Creates a temporary textState. 
   * @param textStateProps values to set in the textState
   */
  const addText = (textStateProps?: CustomisableTextStateProps) => {
    const newText = createText(textStateProps)

    applicationStore.currentEditingText = newText
    applicationStore.editTextState = 'adding'
    applicationStore.selectText(newText)
  }

  /**
   * Saves the textState into the projectStore
   * @param textState
   */
  const saveText = (textState: TextState) => {
    if (applicationStore.editTextState === 'adding') {
      projectStore.textStates.push(textState)
    }
    applicationStore.editTextState = 'none'
  }

  /**
   * Delete the textState from the store
   */
  const deleteText = (textState: TextState) => {
    const findIndex = projectStore.textStates.findIndex((text: TextState) => text.id === textState.id)
    if (findIndex !== -1) {
      projectStore.textStates.splice(findIndex, 1)
    }

    applicationStore.currentEditingText = null
    applicationStore.editTextState = 'none'
  }

  const editText = () => {
    applicationStore.activeActionTab = ActionMenuTabs.TEXT
    applicationStore.currentEditingText = applicationStore.selectedText
    applicationStore.editTextState = 'editing'
  }

  return {
    addText,
    saveText,
    deleteText,
    editText,
    getMaxWidth
  }
}
